import type { Ref } from 'react';
import { forwardRef } from 'react';

import type { BoxProps } from '@/components/Box';

import type { IconSize, IconVariant } from './Icon.types';
import { iconSet } from './icons';
import { IconPixelSize } from './utils';

import * as S from './Icon.styles';

export interface IconProps extends BoxProps {
  name: keyof typeof iconSet;
  size?: IconSize;
  variant?: IconVariant;
}

export const Icon = forwardRef(
  (props: IconProps, ref?: Ref<HTMLDivElement>) => {
    const {
      'aria-hidden': ariaHiddenProp,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      name,
      role: roleProp,
      size = 'm',
      variant = 'regular',
      width,
      ...rest
    } = props;

    const icon = iconSet?.[name]?.[variant]?.[size];

    if (!icon) {
      throw new Error(
        `There's no icon named '${name}', that is of variant '${variant}' and size of '${size}'`,
      );
    }

    const { code } = icon;
    const iconSize = IconPixelSize[size];
    const hasAccessibleName = Boolean(ariaLabel || ariaLabelledBy);
    const ariaHidden = ariaHiddenProp ?? (hasAccessibleName ? undefined : true);
    const role = roleProp ?? (hasAccessibleName ? 'img' : undefined);

    return (
      <S.SVG
        ref={ref}
        {...rest}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-icon-name={name}
        focusable={false}
        height={`${iconSize}px`}
        minWidth={`${width ?? iconSize}px`}
        role={role}
        viewBox={`0 0 ${width ?? iconSize} ${iconSize}`}
        width={`${width ?? iconSize}px`}
        xmlns='http://www.w3.org/2000/svg'
      >
        {code}
      </S.SVG>
    );
  },
);

Icon.displayName = 'Icon';
